import { ApiService } from '../../services/api';
import {
  DailyReportAggregateSnapshot,
  ApiMasterLabelBarcodeLookupResponse,
  ApiMasterLabelUPCData,
  DailyReportSnapshot,
  LiveStatusSnapshot,
  MasterLabelStateSaveRequest,
  MasterLabelStateSaveResponse,
} from '../types';

const API_DL_URL = process.env.REACT_APP_API_DL_URL;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Forge API requests');
}

const inFlightDailyReportRequests = new Map<string, Promise<DailyReportSnapshot>>();
const inFlightLiveStatusRequests = new Map<string, Promise<LiveStatusSnapshot>>();

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: { detail?: string; message?: string } | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as { detail?: string; message?: string };
    } catch {
      payload = null;
    }
  }

  const detail = payload?.detail || payload?.message || rawBody || undefined;
  const message = detail || `${fallback} (${response.status} ${response.statusText})`;

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

export class ForgeApiService {
  static async fetchLiveStatus(token: string, chainId: number): Promise<LiveStatusSnapshot> {
    const requestKey = `${token}:${chainId}`;
    const existingRequest = inFlightLiveStatusRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const request = (async (): Promise<LiveStatusSnapshot> => {
      const response = await fetch(
        `${API_DL_URL}/v1/forge/admin/dashboards/live-status?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching live status snapshot');
      }
      return response.json();
    })();

    inFlightLiveStatusRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightLiveStatusRequests.delete(requestKey);
    }
  }

  static async refreshLiveStatus(token: string, chainId: number): Promise<LiveStatusSnapshot> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/live-status/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chain_id: chainId,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing live status snapshot');
    }
    return response.json();
  }

  static async fetchDailyReport(
    token: string,
    chainId: number,
    date: string
  ): Promise<DailyReportSnapshot> {
    const requestKey = `${token}:${chainId}:${date}`;
    const existingRequest = inFlightDailyReportRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      chain_id: String(chainId),
      date,
    });
    const request = (async (): Promise<DailyReportSnapshot> => {
      const response = await fetch(
        `${API_DL_URL}/v1/forge/admin/dashboards/daily-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching daily report snapshot');
      }
      return response.json();
    })();

    inFlightDailyReportRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDailyReportRequests.delete(requestKey);
    }
  }

  static async refreshDailyReport(
    token: string,
    chainId: number,
    date: string
  ): Promise<DailyReportSnapshot> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/daily-report/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chain_id: chainId,
          date,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing daily report snapshot');
    }
    return response.json();
  }

  static async fetchAggregateReport(
    token: string,
    chainId: number,
    dateFrom: string,
    dateTo: string
  ): Promise<DailyReportAggregateSnapshot> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      date_from: dateFrom,
      date_to: dateTo,
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/aggregate-report?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching aggregate report snapshot');
    }
    return response.json();
  }

  static async fetchItem(
    token: string,
    itemNumber: string,
    chainId: number
  ): Promise<ApiMasterLabelUPCData> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      include_inactive: 'true',
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/item-number/${encodeURIComponent(itemNumber)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data');
    }
    return response.json();
  }

  static async fetchItemByBarcode(
    token: string,
    barcodeQuery: string,
    chainId: number
  ): Promise<ApiMasterLabelBarcodeLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      include_inactive: 'true',
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by barcode');
    }
    return response.json();
  }

  static async saveState(
    token: string,
    request: MasterLabelStateSaveRequest
  ): Promise<MasterLabelStateSaveResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/state`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Master Label state');
    }
    return response.json();
  }
}
